import { DodonaElement } from "@/components/meta/dodona_element";
import { customElement } from "lit/decorators.js";
import { ScrollPin } from "@/scroll_pin";

// Distance below the navbar where a series or section counts as "current".
const TOP_OFFSET = 90;

/**
 * Keeps the active link in the server-rendered course sidebar in sync with the
 * scroll position via an IntersectionObserver over the series cards and section
 * headers, so it copes with cards that grow as they lazy load. A MutationObserver
 * re-points the observer at fresh nodes when lazy-loaded cards are swapped in.
 *
 * Renders no template of its own, so the server-rendered sidebar links stay in
 * place.
 *
 * @element d-course-scrollspy
 */
@customElement("d-course-scrollspy")
export class CourseScrollspy extends DodonaElement {
    private observer: IntersectionObserver | null = null;
    private mutations: MutationObserver | null = null;
    private observed = new Map<string, Element>();
    private regionIds = new WeakMap<Element, string>();
    private orderedIds: string[] = [];
    private visible = new Set<string>();
    private activeId: string | null = null;
    private syncFrame: number | null = null;
    private scrollFrame: number | null = null;
    private pin = new ScrollPin(
        () => this.updateActive(),
        () => this.dispatchEvent(new CustomEvent("sidebar-scroll-end", { bubbles: true })),
    );

    connectedCallback(): void {
        super.connectedCallback();
        this.setup();
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        this.observer?.disconnect();
        this.observer = null;
        this.mutations?.disconnect();
        this.mutations = null;
        if (this.syncFrame !== null) cancelAnimationFrame(this.syncFrame);
        this.syncFrame = null;
        if (this.scrollFrame !== null) cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = null;
        this.pin.clear();
        this.removeEventListener("click", this.onLinkClick);
        window.removeEventListener("scroll", this.onScroll);
        window.removeEventListener("scrollend", this.onScrollEnd);
    }

    private setup(): void {
        this.observer = new IntersectionObserver((entries) => this.onIntersect(entries), {
            rootMargin: `-${TOP_OFFSET}px 0px -70% 0px`,
            threshold: 0,
        });

        this.syncTargets();

        // Series cards lazy load and get their DOM node replaced, so re-point the
        // observer at the new nodes whenever the listing changes.
        const listing = document.getElementById("series-listing");
        if (listing) {
            this.mutations = new MutationObserver(() => this.scheduleSync());
            this.mutations.observe(listing, { childList: true, subtree: true });
        }

        this.addEventListener("click", this.onLinkClick);
        // The observer only reports crossings of the active band, so it says nothing
        // while the page settles against the end of the document.
        window.addEventListener("scroll", this.onScroll, { passive: true });
        window.addEventListener("scrollend", this.onScrollEnd);
    }

    // Pin the clicked target as active for the length of the scroll it starts. The
    // sidebar collapses every section but the active one, so letting the scroll
    // position win on the way there would fold the clicked section shut again and
    // animate every section it passes. The sidebar-scroll-start and
    // sidebar-scroll-end events mark that scroll for the rest of the page; the
    // start event is dispatched on the target, so a collapsed section or series
    // card around it can expand before the browser picks where to scroll to.
    private onLinkClick = (event: MouseEvent): void => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey) return;

        const link = (event.target as Element).closest<HTMLAnchorElement>("a.nav-link");
        const id = link?.getAttribute("href")?.slice(1);
        if (!id || !this.observed.has(id)) return;

        /**
         * The pin normally lifts on arrival; its own release catches a scroll that
         * never arrives because the user took over halfway, or never starts at all.
         */
        this.pin.pin(id);
        this.setActive(id);
        document.getElementById(id)?.dispatchEvent(new CustomEvent("sidebar-scroll-start", { bubbles: true }));
    };

    private onScrollEnd = (): void => this.pin.scrollEnded();

    private onScroll = (): void => {
        this.pin.scrolled();
        if (this.scrollFrame !== null) return;
        this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = null;
            this.updateActive();
        });
    };

    // A burst of mutations (several cards resolving, or deep changes inside a
    // loaded card) collapses to a single rescan on the next frame. The rescan is
    // idempotent, so coalescing only saves work.
    private scheduleSync(): void {
        if (this.syncFrame !== null) return;
        this.syncFrame = requestAnimationFrame(() => {
            this.syncFrame = null;
            this.syncTargets();
        });
    }

    private syncTargets(): void {
        const anchors = document.querySelectorAll<HTMLElement>(
            "#series-listing .series .anchor, #series-listing .series-section > .anchor",
        );

        const seen = new Set<string>();
        const order: string[] = [];
        anchors.forEach((anchor) => {
            if (!anchor.id) return;
            const region = this.regionFor(anchor);

            order.push(anchor.id);
            seen.add(anchor.id);

            const current = this.observed.get(anchor.id);
            if (current === region) return;
            if (current) this.observer.unobserve(current);
            this.observer.observe(region);
            this.observed.set(anchor.id, region);
            this.regionIds.set(region, anchor.id);
        });

        // Drop targets whose card disappeared so stale ids can't stay active.
        for (const [id, region] of this.observed) {
            if (seen.has(id)) continue;
            this.observer.unobserve(region);
            this.observed.delete(id);
            this.visible.delete(id);
        }

        this.orderedIds = order;
        this.updateActive();
    }

    // Anchors have no height, so track the enclosing series card, or the header
    // for a section anchor, mirroring how the sidebar links are grouped.
    private regionFor(anchor: HTMLElement): Element {
        return (
            anchor.closest(".series") ??
            anchor.closest(".series-section")?.querySelector(".series-section-header") ??
            anchor
        );
    }

    private onIntersect(entries: IntersectionObserverEntry[]): void {
        for (const entry of entries) {
            const id = this.regionIds.get(entry.target);
            if (id === undefined) continue;
            // Ignore entries from a card that has since been swapped out.
            if (this.observed.get(id) !== entry.target) continue;

            if (entry.isIntersecting) {
                this.visible.add(id);
            } else {
                this.visible.delete(id);
            }
        }
        this.updateActive();
    }

    private updateActive(): void {
        const active = this.computeActive();

        if (this.pin.id !== null) {
            // The scroll the pin was made for hasn't landed yet.
            if (active !== this.pin.id) return;
            this.pin.clear();
        }

        // Keep the current link highlighted in the gaps between cards where
        // nothing crosses the active band.
        if (active === null || active === this.activeId) return;
        this.setActive(active);
    }

    private computeActive(): string | null {
        // Against the end of the document the remaining targets can no longer reach
        // the band, so the last one owns the reading position however short it is.
        if (this.atDocumentEnd()) return this.orderedIds.at(-1) ?? null;

        // The band is taller than a collapsed card, so several targets can cross it
        // at once. The topmost one owns the reading position.
        return this.orderedIds.find((id) => this.visible.has(id)) ?? null;
    }

    private atDocumentEnd(): boolean {
        const { scrollHeight } = document.documentElement;
        return window.scrollY + window.innerHeight >= scrollHeight - 1;
    }

    private setActive(id: string | null): void {
        if (this.activeId) {
            const previous = this.linkFor(this.activeId);
            previous?.classList.remove("active");
            previous?.removeAttribute("aria-current");
        }

        this.activeId = id;
        const link = id ? this.linkFor(id) : null;
        if (link) {
            link.classList.add("active");
            link.setAttribute("aria-current", "location");
        }
    }

    private linkFor(id: string): HTMLAnchorElement | null {
        const link = this.querySelector<HTMLAnchorElement>(`a[href="#${CSS.escape(id)}"]`);
        if (!link?.classList.contains("section-header")) return link;

        // A section header has no active style of its own, so highlight the first
        // series of the section instead.
        const group = link.closest(".section-group");
        return group?.querySelector<HTMLAnchorElement>(".section-children .nav-link") ?? link;
    }
}
